import React from "react";
import { isSet } from "../../../../../../utils/commonUtils";


const TrialYesOrNoTable = ({
    label,
    sections = [],
    list = [],
    options = [{ label: 'Yes', value: 1 }, { label: 'No', value: 0 }],
    formData = {},
    changeHandler,
    updateFormData,
    readOnly,
    validation = {},
    appendClass = '',
    columns,
    // If not provided, defaults to trials [1, 2, 3] for backward compatibility
    ...props
}) => {
    // Normalize: if list is used (single section), treat as one section with no sectionLabel
    const normalizedSections = sections.length > 0
        ? sections
        : [{ rows: list }];

    // Determine columns: use provided columns or default to trials [1, 2, 3]
    const defaultColumns = [1, 2, 3].map(num => ({ label: String(num), value: num }));
    const activeColumns = columns || defaultColumns;
    const isTrialMode = !columns; // If columns not provided, use trial mode

    const getFieldName = (row, columnValue) => {
        if (isTrialMode) {
            // Original trial mode behavior
            if (row.nameTrial1 !== undefined) return row[`nameTrial${columnValue}`];
            return `${row.nameBase}_trial_${columnValue}`;
        } else {
            // New column mode: use column value (e.g., 'left', 'right')
            return `${row.nameBase}_${columnValue}`;
        }
    };

    const allDataRows = normalizedSections.flatMap(s => (s.rows || []).filter(r => r.nameBase || r.nameTrial1));

    const handleHeaderBulk = (columnValue, optionValue) => {
        if (readOnly || !updateFormData) return;
        const updated = {};
        allDataRows.forEach(row => {
            updated[getFieldName(row, columnValue)] = optionValue;
        });
        updateFormData(updated);
    };

    const isHeaderColumnSelected = (columnValue, optionValue) => {
        if (allDataRows.length === 0) return false;
        return allDataRows.every(row => isSet(formData[getFieldName(row, columnValue)], null) === optionValue);
    };

    const headerBg = { backgroundColor: '#F0F3FD' };
    const headingColor = { color: '#12203E' };
    const innerTextColor = { color: '#1D2433' };

    return (
        <div className={`border-0 shadow-none h-auto mb-0 pt-2 pb-3 ${appendClass}`} style={{ paddingLeft: '0rem' }}>
            <div className="table-responsive">
                <table className="table table-sm mb-0 align-middle border-0">
                    <thead style={headerBg}>
                        <tr>
                            <th rowSpan={2} className="fw-semibold text-md border-0 align-middle" style={{ minWidth: '160px', ...headerBg, ...headingColor }}>
                                {label}
                            </th>
                            {activeColumns.map((column) => (
                                <th key={column.value} colSpan={2} className="text-center border-0 p-1 ps-2 pt-2 fw-semibold text-sm" style={{ ...headerBg, ...innerTextColor }}>
                                    {column.label}
                                </th>
                            ))}
                        </tr>
                        <tr style={headerBg}>
                            {activeColumns.map((column) => (
                                <React.Fragment key={column.value}>
                                    {options.map((option) => {
                                        const checked = isHeaderColumnSelected(column.value, option.value);
                                        const headerName = isTrialMode ? `header_trial_${column.value}` : `header_${column.value}`;
                                        const headerId = isTrialMode ? `header_t${column.value}_${option.value}` : `header_${column.value}_${option.value}`;
                                        return (
                                            <th key={`${column.value}-${option.value}`} className="text-center border-0 p-1" style={{ ...headerBg, ...innerTextColor }}>
                                                <div className="form-check form-check-inline d-inline-flex justify-content-center align-items-center mb-0" style={{ marginLeft: '1.4rem' }}>
                                                    <input
                                                        className="form-check-input cursor-pointer"
                                                        type="radio"
                                                        name={headerName}
                                                        id={headerId}
                                                        checked={checked}
                                                        readOnly={readOnly}
                                                        disabled={readOnly}
                                                        onChange={() => handleHeaderBulk(column.value, option.value)}
                                                    />
                                                    <label className="form-check-label text-sm mb-0 cursor-pointer ms-1" htmlFor={headerId} style={innerTextColor}>
                                                        {option.label}
                                                    </label>
                                                </div>
                                            </th>
                                        );
                                    })}
                                </React.Fragment>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {normalizedSections.map((section, sIdx) => (
                            <React.Fragment key={sIdx}>
                                {section.sectionLabel && (
                                    <tr>
                                        <td colSpan={activeColumns.length * 2 + 1} className="fw-semibold text-md border-0 pt-2 pb-1" style={headingColor}>
                                            {section.sectionLabel}
                                        </td>
                                    </tr>
                                )}
                                {(section.rows || []).map((row, rowIndex) => (
                                    <tr key={`${sIdx}-${rowIndex}`}>
                                        <td className="fw-normal text-sm border-0 p-1" style={innerTextColor}>
                                            {row.label}
                                            {activeColumns.some(col => isSet(validation[getFieldName(row, col.value)], '') !== '') && (
                                                <span className="text-danger ml-1 small">
                                                    ({activeColumns.map(col => validation[getFieldName(row, col.value)]).filter(Boolean).join(', ')})
                                                </span>
                                            )}
                                        </td>
                                        {activeColumns.map((column) => {
                                            const name = getFieldName(row, column.value);
                                            const value = isSet(formData[name], null);
                                            return (
                                                <React.Fragment key={column.value}>
                                                    {options.map((option, optIndex) => {
                                                        const isSelected = value === option.value;
                                                        return (
                                                            <td key={optIndex} className="text-center py-1 border-0">
                                                                <div className="form-check form-check-inline d-inline-flex justify-content-center mb-0">
                                                                    <input
                                                                        className="form-check-input cursor-pointer"
                                                                        type="radio"
                                                                        name={name}
                                                                        id={`${name}_${optIndex}`}
                                                                        checked={isSelected}
                                                                        readOnly={readOnly}
                                                                        disabled={readOnly}
                                                                        onChange={() => changeHandler && changeHandler(name, option.value)}
                                                                        title={option.label}
                                                                    />
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

TrialYesOrNoTable.defaultProps = {
    appendClass: 'mt-0',
};

export default TrialYesOrNoTable;
